import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Star,
  EyeOff,
  Users,
  Zap,
  CheckCircle2,
  XCircle,
  Minus,
  Images,
  Target,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { usePhotos, usePhoto, useUpdatePhoto } from '@/api/hooks/usePhotos';
import { useSimilarityGroup } from '@/api/hooks/useSimilarity';
import { getPhotoThumbnailUrl } from '@/api/services/photo.service';
import type { PhotoListItem } from '@/api/types/photo.types';

// ── Helpers ───────────────────────────────────────────────────────────────────

type Decision = 'starred' | 'rejected' | null;

function scoreColor(score: number | null): string {
  if (score === null) return 'bg-gray-300 dark:bg-gray-600';
  if (score >= 75) return 'bg-green-500';
  if (score >= 50) return 'bg-amber-400';
  return 'bg-red-500';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ScoreRow({
  label,
  value,
  max = 100,
  invert = false,
}: {
  label: string;
  value: number | null;
  max?: number;
  invert?: boolean;
}) {
  const normalized = value === null ? null : invert ? Math.max(0, max - value) : value;
  const pct = normalized === null ? 0 : Math.min(100, (normalized / max) * 100);
  const colorClass = scoreColor(normalized);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className="font-mono font-medium text-gray-700 dark:text-gray-300">
          {value === null ? '—' : Math.round(value)}
        </span>
      </div>
      <div className="h-1 w-full rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className={`h-full rounded-full transition-all duration-300 ${colorClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function KeyHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-1">
        {keys.map((k) => (
          <kbd
            key={k}
            className="inline-flex h-5 min-w-[20px] items-center justify-center rounded border border-gray-300 bg-white px-1 font-mono text-[10px] text-gray-600 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
          >
            {k}
          </kbd>
        ))}
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    </div>
  );
}

// ── Similarity strip ──────────────────────────────────────────────────────────

function SimilarityStrip({ groupId, currentPhotoId }: { groupId: string; currentPhotoId: string }) {
  const { data: group } = useSimilarityGroup(groupId);
  if (!group || group.photos.length <= 1) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <Images className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          Similar group ({group.photo_count})
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {group.photos.map((p) => (
          <div
            key={p.id}
            className={`relative h-12 w-12 shrink-0 overflow-hidden rounded-md border-2 transition-all ${
              p.id === currentPhotoId
                ? 'border-primary-500'
                : p.is_group_best
                ? 'border-green-500'
                : 'border-transparent opacity-60'
            }`}
          >
            <img
              src={getPhotoThumbnailUrl(p.id, 'small')}
              alt=""
              className="h-full w-full object-cover"
            />
            {p.is_group_best && p.id !== currentPhotoId && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Target className="h-3 w-3 text-green-400" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function CullingPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event') ?? undefined;
  const eventTitle = searchParams.get('title') ?? 'Event';

  const [currentIndex, setCurrentIndex] = useState(0);
  // Local decision overlay (before they're persisted or in addition to server state)
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const imgRef = useRef<HTMLImageElement>(null);

  // ── Data ────────────────────────────────────────────────────────────────────

  // Load all photos sorted by quality desc for culling
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = usePhotos({
    event: eventId,
    ordering: '-quality_score',
    page_size: 100,
  });

  const photos: PhotoListItem[] = useMemo(
    () => data?.pages.flatMap((p) => p.results) ?? [],
    [data],
  );

  // Eagerly load all pages
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const currentPhoto = photos[currentIndex];
  const nextPhoto = photos[currentIndex + 1];

  // Full detail for current photo (quality scores, EXIF, similarity group)
  const { data: detail } = usePhoto(currentPhoto?.id ?? '');
  // Prefetch next photo
  usePhoto(nextPhoto?.id ?? '');

  const updateMutation = useUpdatePhoto();

  // ── Navigation ──────────────────────────────────────────────────────────────

  const goNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(i + 1, photos.length - 1));
  }, [photos.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => Math.max(i - 1, 0));
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleStar = useCallback(() => {
    if (!currentPhoto) return;
    const currentDecision = decisions[currentPhoto.id];
    const isAlreadyStarred = currentDecision === 'starred' || (currentDecision === undefined && currentPhoto.is_starred);

    if (isAlreadyStarred) {
      // Toggle off
      updateMutation.mutate({ photoId: currentPhoto.id, payload: { is_starred: false } });
      setDecisions((prev) => ({ ...prev, [currentPhoto.id]: null }));
    } else {
      updateMutation.mutate({ photoId: currentPhoto.id, payload: { is_starred: true, is_hidden: false } });
      setDecisions((prev) => ({ ...prev, [currentPhoto.id]: 'starred' }));
      toast.success('Photo starred', { duration: 1000 });
      goNext();
    }
  }, [currentPhoto, decisions, updateMutation, goNext]);

  const handleReject = useCallback(() => {
    if (!currentPhoto) return;
    const currentDecision = decisions[currentPhoto.id];
    const isAlreadyRejected = currentDecision === 'rejected' || (currentDecision === undefined && currentPhoto.is_hidden);

    if (isAlreadyRejected) {
      updateMutation.mutate({ photoId: currentPhoto.id, payload: { is_hidden: false } });
      setDecisions((prev) => ({ ...prev, [currentPhoto.id]: null }));
    } else {
      updateMutation.mutate({ photoId: currentPhoto.id, payload: { is_hidden: true } });
      setDecisions((prev) => ({ ...prev, [currentPhoto.id]: 'rejected' }));
      toast.error('Photo rejected', { duration: 1000 });
      goNext();
    }
  }, [currentPhoto, decisions, updateMutation, goNext]);

  // ── Keyboard shortcuts ───────────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          goNext();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          goPrev();
          break;
        case ' ':
          e.preventDefault();
          goNext();
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          handleStar();
          break;
        case 'r':
        case 'R':
        case 'x':
        case 'X':
          e.preventDefault();
          handleReject();
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, handleStar, handleReject]);

  // ── Derived state ───────────────────────────────────────────────────────────

  const effectiveDecision = currentPhoto
    ? (decisions[currentPhoto.id] !== undefined
        ? decisions[currentPhoto.id]
        : currentPhoto.is_starred
        ? 'starred'
        : currentPhoto.is_hidden
        ? 'rejected'
        : null)
    : null;

  const starredCount = photos.filter(
    (p) => decisions[p.id] === 'starred' || (decisions[p.id] === undefined && p.is_starred),
  ).length;

  const rejectedCount = photos.filter(
    (p) => decisions[p.id] === 'rejected' || (decisions[p.id] === undefined && p.is_hidden),
  ).length;

  const pendingCount = photos.length - starredCount - rejectedCount;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
        <p className="text-gray-500 dark:text-gray-400">{t('culling.noPhotos')}</p>
        <Link
          to={eventId ? `/gallery?event=${eventId}` : '/gallery'}
          className="btn-secondary"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('culling.backToGallery')}
        </Link>
      </div>
    );
  }

  const thumbnailUrl = currentPhoto
    ? getPhotoThumbnailUrl(currentPhoto.id, 'large')
    : '';

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col overflow-hidden -m-6">
      {/* ── Header bar ── */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-2 dark:border-surface-border dark:bg-surface-sidebar">
        <div className="flex items-center gap-3">
          <Link
            to={eventId ? `/gallery?event=${eventId}` : '/gallery'}
            className="flex items-center gap-1.5 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-surface-hover dark:hover:text-gray-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-medium">{t('culling.exitCulling')}</span>
          </Link>
          <div className="h-4 w-px bg-gray-200 dark:bg-surface-border" />
          <div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">{eventTitle}</span>
            <span className="ml-2 text-xs text-gray-400">— {t('culling.mode')}</span>
          </div>
        </div>

        {/* Progress stats */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
            <span className="font-semibold text-gray-700 dark:text-gray-300">{starredCount}</span>
            <span className="dark:text-gray-400">{t('culling.starred')}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <XCircle className="h-3.5 w-3.5 text-red-500" />
            <span className="font-semibold text-gray-700 dark:text-gray-300">{rejectedCount}</span>
            <span className="dark:text-gray-400">{t('culling.rejected')}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Minus className="h-3.5 w-3.5 text-gray-400" />
            <span className="font-semibold text-gray-700 dark:text-gray-300">{pendingCount}</span>
            <span className="dark:text-gray-400">{t('culling.pending')}</span>
          </div>
          <div className="h-4 w-px bg-gray-200 dark:bg-surface-border" />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {currentIndex + 1} / {photos.length}
          </span>
          {/* Progress bar */}
          <div className="w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-1.5 rounded-full bg-primary-500 transition-all"
              style={{ width: `${((currentIndex + 1) / photos.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── Body: photo + sidebar ── */}
      <div className="flex min-h-0 flex-1">
        {/* Photo area */}
        <div className="relative flex flex-1 items-center justify-center bg-gray-950">
          {/* Status overlay badge */}
          {effectiveDecision && (
            <div
              className={`pointer-events-none absolute left-4 top-4 z-10 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold shadow-lg ${
                effectiveDecision === 'starred'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-red-500 text-white'
              }`}
            >
              {effectiveDecision === 'starred' ? (
                <>
                  <Star className="h-3.5 w-3.5 fill-current" />
                  {t('gallery.starred')}
                </>
              ) : (
                <>
                  <EyeOff className="h-3.5 w-3.5" />
                  {t('gallery.hidden')}
                </>
              )}
            </div>
          )}

          {/* Quality score badge */}
          {currentPhoto?.quality_score !== null && currentPhoto?.quality_score !== undefined && (
            <div className="pointer-events-none absolute right-4 top-4 z-10 flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 backdrop-blur-sm">
              <Zap className="h-3 w-3 text-yellow-400" />
              <span className="text-xs font-bold text-white">{Math.round(currentPhoto.quality_score)}</span>
            </div>
          )}

          {/* Main image */}
          <img
            ref={imgRef}
            key={currentPhoto?.id}
            src={thumbnailUrl}
            alt={currentPhoto?.original_filename}
            className={`max-h-full max-w-full object-contain transition-all duration-150 ${
              effectiveDecision === 'rejected' ? 'opacity-40 grayscale' : ''
            }`}
          />

          {/* Prev button */}
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="absolute left-3 top-1/2 -translate-y-1/2 rounded-xl bg-black/50 p-2.5 text-white backdrop-blur-sm transition-all hover:bg-black/70 disabled:opacity-20"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          {/* Next button */}
          <button
            onClick={goNext}
            disabled={currentIndex >= photos.length - 1}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-xl bg-black/50 p-2.5 text-white backdrop-blur-sm transition-all hover:bg-black/70 disabled:opacity-20"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Filename */}
          <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 backdrop-blur-sm">
            <span className="text-xs font-mono text-white/80">{currentPhoto?.original_filename}</span>
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex w-72 shrink-0 flex-col gap-4 overflow-y-auto border-l border-gray-200 bg-white p-4 dark:border-surface-border dark:bg-surface-sidebar">

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleStar}
              className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-semibold transition-all ${
                effectiveDecision === 'starred'
                  ? 'border-yellow-400 bg-yellow-400/10 text-yellow-600 dark:border-yellow-500 dark:text-yellow-400'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-yellow-400 hover:text-yellow-600 dark:border-surface-border dark:bg-surface-card dark:text-gray-400 dark:hover:border-yellow-500 dark:hover:text-yellow-400'
              }`}
            >
              <Star className={`h-4 w-4 ${effectiveDecision === 'starred' ? 'fill-current' : ''}`} />
              {t('culling.star')}
            </button>
            <button
              onClick={handleReject}
              className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-semibold transition-all ${
                effectiveDecision === 'rejected'
                  ? 'border-red-500 bg-red-500/10 text-red-600 dark:border-red-500 dark:text-red-400'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-red-400 hover:text-red-600 dark:border-surface-border dark:bg-surface-card dark:text-gray-400 dark:hover:border-red-500 dark:hover:text-red-400'
              }`}
            >
              <EyeOff className="h-4 w-4" />
              {t('culling.reject')}
            </button>
          </div>

          {/* Quality scores */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-surface-border dark:bg-surface-card">
            <div className="mb-3 flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-primary-500" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('culling.qualityAnalysis')}
              </span>
            </div>

            {detail ? (
              <div className="space-y-3">
                {/* Overall */}
                <div className="mb-2 flex items-end justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{t('gallery.overall')}</span>
                  <div className="text-right">
                    <span className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">
                      {detail.quality_score !== null ? Math.round(detail.quality_score) : '—'}
                    </span>
                    <span className="ml-1 text-xs text-gray-400">/100</span>
                  </div>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className={`h-full rounded-full transition-all ${scoreColor(detail.quality_score)}`}
                    style={{ width: `${detail.quality_score ?? 0}%` }}
                  />
                </div>

                <div className="mt-3 space-y-2.5 border-t border-gray-200 pt-3 dark:border-surface-border">
                  <ScoreRow label={t('gallery.sharpness')} value={detail.sharpness_score} />
                  <ScoreRow label={t('gallery.exposure')} value={detail.exposure_score} />
                  <ScoreRow label={t('gallery.noise')} value={detail.noise_score} />
                  <ScoreRow label={t('gallery.blur')} value={detail.blur_score} invert />
                </div>

                {/* Flags */}
                <div className="mt-2 flex flex-wrap gap-1.5 border-t border-gray-200 pt-3 dark:border-surface-border">
                  {detail.is_blurry && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                      {t('gallery.blurry')}
                    </span>
                  )}
                  {detail.is_overexposed && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      {t('gallery.overexposed')}
                    </span>
                  )}
                  {detail.is_underexposed && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      {t('gallery.underexposed')}
                    </span>
                  )}
                  {!detail.is_blurry && !detail.is_overexposed && !detail.is_underexposed && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      ✓ {t('status.success')}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                ))}
              </div>
            )}
          </div>

          {/* Faces */}
          {(currentPhoto?.face_count ?? 0) > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-surface-border dark:bg-surface-card">
              <Users className="h-4 w-4 text-gray-400" />
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {t('gallery.faceCount', { count: currentPhoto!.face_count })}
              </span>
            </div>
          )}

          {/* Best in group */}
          {currentPhoto?.is_group_best && (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-900/20">
              <Target className="h-4 w-4 text-green-500" />
              <span className="text-xs font-medium text-green-700 dark:text-green-400">
                {t('culling.bestInGroup')}
              </span>
            </div>
          )}

          {/* Similarity group */}
          {detail?.similarity_group_id && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-surface-border dark:bg-surface-card">
              <SimilarityStrip
                groupId={detail.similarity_group_id}
                currentPhotoId={detail.id}
              />
            </div>
          )}

          {/* EXIF summary */}
          {detail && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-surface-border dark:bg-surface-card">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                {t('gallery.camera')}
              </span>
              <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                {detail.camera_model_name && (
                  <div className="truncate">{detail.camera_model_name}</div>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
                  {detail.aperture && <span>f/{detail.aperture}</span>}
                  {detail.shutter_speed && <span>{detail.shutter_speed}s</span>}
                  {detail.iso && <span>ISO {detail.iso}</span>}
                  {detail.focal_length_mm && <span>{detail.focal_length_mm}mm</span>}
                </div>
              </div>
            </div>
          )}

          {/* Keyboard shortcuts */}
          <div className="mt-auto rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-surface-border dark:bg-surface-card">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('culling.shortcuts')}
            </span>
            <div className="space-y-1.5">
              <KeyHint keys={['A']} label={t('culling.starShortcut')} />
              <KeyHint keys={['R']} label={t('culling.rejectShortcut')} />
              <KeyHint keys={['←', '→']} label={t('culling.navigate')} />
              <KeyHint keys={['Space']} label={t('culling.nextShortcut')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
